import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cartLineKey, useCart } from "@/context/CartContext";
import { useScrollLock } from "@/utils/useScrollLock";
import { formatBRL } from "@/utils/format";
import { createNuvemshopCheckout, isNuvemshopEnabled } from "@/services/nuvemshop";
import { hasCartContact, sendCartSnapshot } from "@/services/cartTracking";

const CHECKOUT_STORAGE_KEY = "rt_checkout_url";
const MIN_ORDER_VALUE = 49;

export default function CartDrawer() {
  const navigate = useNavigate();
  const { items, isOpen, closeCart, removeItem, updateQuantity, totalPrice } = useCart();
  useScrollLock(isOpen);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const trackingTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Visibilidade de carrinhos com item que nunca viram pedido: o catálogo e o
  // carrinho são 100% customizados, então a Nuvemshop não tem como enxergar
  // isso sozinha. Envio best-effort, com debounce pra não disparar a cada tecla.
  useEffect(() => {
    // Só rastreia carrinho identificado (e-mail ou telefone válido): anônimo não é enviado.
    if (items.length === 0 || !hasCartContact(email, phone)) return;
    clearTimeout(trackingTimer.current);
    trackingTimer.current = setTimeout(() => {
      sendCartSnapshot({
        items: items.map((item) => ({ name: item.variantLabel ? `${item.name} (${item.variantLabel})` : item.name, quantity: item.quantity })),
        subtotal: totalPrice,
        email: email.trim() || undefined,
        name: fullName.trim() || undefined,
        phone: phone.trim() || undefined,
      });
    }, 2500);
    return () => clearTimeout(trackingTimer.current);
  }, [items, totalPrice, email, fullName, phone]);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const belowMinimum = totalPrice > 0 && totalPrice < MIN_ORDER_VALUE;

  const handleCheckout = async () => {
    if (isRedirecting) return;
    if (belowMinimum) return;
    if (!isNuvemshopEnabled()) {
      setCheckoutError("Integração com a Nuvemshop ainda não configurada.");
      return;
    }
    if (!fullName.trim().includes(" ")) {
      setCheckoutError("Informe nome e sobrenome para continuar.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setCheckoutError("Informe um e-mail válido para continuar.");
      return;
    }
    if (phone.replace(/\D/g, "").length < 10) {
      setCheckoutError("Informe um WhatsApp válido com DDD para continuar.");
      return;
    }
    setCheckoutError(null);
    setIsRedirecting(true);
    try {
      const checkoutUrl = await createNuvemshopCheckout(items, { email, fullName, phone });
      sessionStorage.setItem(CHECKOUT_STORAGE_KEY, checkoutUrl);
      closeCart();
      navigate("/checkout");
    } catch (error) {
      setCheckoutError(error instanceof Error ? error.message : "Não foi possível iniciar o checkout.");
      setIsRedirecting(false);
    }
  };

  return (
    <>
      <div
        onClick={closeCart}
        className={`fixed inset-0 z-50 bg-foreground-950/40 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      ></div>
      <aside
        className={`cart-panel fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-background-50 transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
          <h3 className="font-heading text-xl text-foreground-950">Sua Sacola</h3>
          <button
            type="button"
            aria-label="Fechar carrinho"
            onClick={closeCart}
            className="flex h-11 w-11 items-center justify-center rounded-full text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-primary-500">
                <i className="ri-shopping-bag-line text-3xl"></i>
              </span>
              <p className="mt-4 text-sm text-foreground-600">Sua sacola está vazia.</p>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li key={cartLineKey(item)} className="flex gap-3">
                  <div className="h-24 w-20 shrink-0 overflow-hidden rounded-md bg-background-100">
                    <img src={item.image} alt={item.name} className="h-full w-full object-cover object-top" />
                  </div>
                  <div className="flex flex-1 flex-col">
                    <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                    {item.variantLabel && <p className="mt-0.5 text-xs text-foreground-600">{item.variantLabel}</p>}
                    <p className="mt-0.5 text-sm text-primary-700">{formatBRL(item.price)}</p>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center rounded-md border border-background-300">
                        <button
                          type="button"
                          aria-label="Diminuir quantidade"
                          onClick={() => updateQuantity(cartLineKey(item), item.quantity - 1)}
                          className="flex h-7 w-7 items-center justify-center text-foreground-700 hover:text-primary-700"
                        >
                          <i className="ri-subtract-line text-sm"></i>
                        </button>
                        <span className="w-7 text-center text-sm text-foreground-900">{item.quantity}</span>
                        <button
                          type="button"
                          aria-label="Aumentar quantidade"
                          onClick={() => updateQuantity(cartLineKey(item), item.quantity + 1)}
                          disabled={item.maxQuantity !== undefined && item.quantity >= item.maxQuantity}
                          className="flex h-7 w-7 items-center justify-center text-foreground-700 hover:text-primary-700"
                        >
                          <i className="ri-add-line text-sm"></i>
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(cartLineKey(item))}
                        className="text-xs text-foreground-500 hover:text-accent-600"
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-background-200 px-5 py-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm text-foreground-600">Subtotal</span>
            <span className="font-heading text-xl text-foreground-950">{formatBRL(totalPrice)}</span>
          </div>
          {belowMinimum && (
            <p className="mb-3 rounded-md bg-background-100 px-3 py-2.5 text-xs text-foreground-700">
              Faltam <strong>{formatBRL(MIN_ORDER_VALUE - totalPrice)}</strong> para o pedido mínimo de {formatBRL(MIN_ORDER_VALUE)}.
            </p>
          )}
          {items.length > 0 && !belowMinimum && (
            <>
              <input
                type="text"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                aria-label="Nome completo" autoComplete="name" placeholder="Nome completo"
                className="mb-2 w-full rounded-md border border-background-300 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none"
              />
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                aria-label="Seu e-mail para o pedido" autoComplete="email" placeholder="Seu e-mail para o pedido"
                className="mb-2 w-full rounded-md border border-background-300 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none"
              />
              <input
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                aria-label="Seu WhatsApp" autoComplete="tel" placeholder="Seu WhatsApp com DDD"
                className="mb-3 w-full rounded-md border border-background-300 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none"
              />
            </>
          )}
          {checkoutError && (
            <p role="alert" className="mb-2 text-xs text-accent-600">{checkoutError}</p>
          )}
          {!belowMinimum && (
            <button
              type="button"
              disabled={items.length === 0 || isRedirecting}
              onClick={handleCheckout}
              className="w-full whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isRedirecting ? "Redirecionando..." : "Finalizar compra"}
            </button>
          )}
          <button
            type="button"
            onClick={closeCart}
            className="mt-2 w-full whitespace-nowrap rounded-md border border-background-300 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            Continuar comprando
          </button>
        </div>
      </aside>
    </>
  );
}
